/**
 * Shared Schedule Grid Component
 *
 * Reusable visual grid for displaying class schedules.
 * Can be configured for both editable (admin) and read-only (public) modes.
 * Supports studio theming for public displays.
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Paper,
  Button,
  ButtonGroup,
  IconButton,
  Slider,
  Typography,
  Alert,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import {
  DndContext,
  pointerWithin,
  // useDraggable will be used for ClassBlock components
  // useDroppable will be used for GridCell components
  // DragOverlay will be used for drag preview
} from '@dnd-kit/core';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import {
  DAYS_OF_WEEK,
  formatTimeDisplay,
  generateTimeSlots,
  getDefaultTimeRange,
} from '../tabs/ScheduleGridTab/utils/timeUtils';
import {
  timeToMinutes,
  detectConcurrentClasses,
  calculateColumnPosition,
} from '../tabs/ScheduleGridTab/utils/classPositioning';
import {
  type ColorCodeMode,
  createColorMap,
  getColorForClass,
  loadColorModePreference,
  saveColorModePreference,
} from '../utils/colorUtils';
import DraggableClassBlock from './DraggableClassBlock';
import DroppableGridCell from './DroppableGridCell';
import ColorLegend from './ColorLegend';
import type { ScheduledClass } from '../../../types/scheduling';

export interface ScheduleGridTheme {
  primaryColor: string;
  fontFamily: string;
  textColor: string;
  backgroundColor: string;
}

export interface ScheduleGridProps {
  scheduledClasses: ScheduledClass[];
  isEditable?: boolean;
  theme?: ScheduleGridTheme;
  onCellClick?: (day: string, time: string) => void;
  onClassClick?: (scheduledClass: ScheduledClass) => void;
  // Drag-and-drop props
  onDragStart?: (event: DragStartEvent) => void;
  onDragEnd?: (event: DragEndEvent) => void;
  activeClassId?: string | null;
}

type TimeIncrement = 15 | 30 | 60;

const ScheduleGrid: React.FC<ScheduleGridProps> = ({
  scheduledClasses,
  isEditable = false,
  theme,
  onCellClick,
  onClassClick,
  onDragStart,
  onDragEnd,
  activeClassId: _activeClassId, // Will be used for drag overlay
}) => {
  // Get MUI theme
  const muiTheme = useTheme();

  // Time range state (hours in 24-hour format)
  const defaultRange = getDefaultTimeRange();
  const [startHour, setStartHour] = useState(defaultRange.startHour);
  const [endHour, setEndHour] = useState(defaultRange.endHour);

  // Zoom state (time increment in minutes)
  const [timeIncrement, setTimeIncrement] = useState<TimeIncrement>(60);

  // Visible days state
  const [numVisibleDays, setNumVisibleDays] = useState(7);
  const [startDayIndex, setStartDayIndex] = useState(0);

  // Color coding mode state
  const [colorMode, setColorMode] = useState<ColorCodeMode>(() =>
    loadColorModePreference()
  );

  // Save color mode preference when it changes
  useEffect(() => {
    saveColorModePreference(colorMode);
  }, [colorMode]);

  const timeSlots = generateTimeSlots(startHour, endHour, timeIncrement);
  const visibleDays = DAYS_OF_WEEK.slice(
    startDayIndex,
    startDayIndex + numVisibleDays
  );

  // Filter classes to only those within the visible time range
  const visibleClasses = scheduledClasses.filter(sc => {
    const classStartTime = sc.start_time.substring(0, 5);
    const [hours] = classStartTime.split(':').map(Number);
    return hours >= startHour && hours < endHour;
  });

  // Apply theme colors - prioritize custom theme, fallback to MUI theme
  const primaryColor = theme?.primaryColor || muiTheme.palette.primary.main;
  const fontFamily = theme?.fontFamily || muiTheme.typography.fontFamily;
  const textColor = theme?.textColor || muiTheme.palette.text.primary;
  const backgroundColor =
    theme?.backgroundColor || muiTheme.palette.background.paper;

  // Generate color map based on color mode
  const colorMap = useMemo(() => {
    const items: string[] = [];

    scheduledClasses.forEach(sc => {
      switch (colorMode) {
        case 'styles':
          if (sc.class_details.styles?.[0]?.id) {
            items.push(sc.class_details.styles[0].id);
          }
          break;
        case 'teachers':
          if (sc.teachers?.[0]) {
            items.push(sc.teachers[0]);
          }
          break;
        case 'rooms':
          items.push(sc.room);
          break;
        case 'disciplines':
          if (sc.class_details.styles?.[0]?.disciplines?.[0]) {
            items.push(sc.class_details.styles[0].disciplines[0]);
          }
          break;
        default:
          break;
      }
    });

    return createColorMap(items, primaryColor);
  }, [scheduledClasses, colorMode, primaryColor]);

  const handleCellClick = (day: string, time: string) => {
    if (isEditable && onCellClick) {
      onCellClick(day, time);
    }
  };

  const handleClassBlockClick = (
    event: React.MouseEvent,
    scheduledClass: ScheduledClass
  ) => {
    event.stopPropagation();
    if (isEditable && onClassClick) {
      onClassClick(scheduledClass);
    }
  };

  const handleZoomIn = () => {
    if (timeIncrement === 60) setTimeIncrement(30);
    else if (timeIncrement === 30) setTimeIncrement(15);
  };

  const handleZoomOut = () => {
    if (timeIncrement === 15) setTimeIncrement(30);
    else if (timeIncrement === 30) setTimeIncrement(60);
  };

  const handleTimeRangeChange = (event: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      setStartHour(newValue[0]);
      setEndHour(newValue[1]);
    }
  };

  const handleQuickRange = (
    preset: 'morning' | 'afternoon' | 'evening' | 'full'
  ) => {
    switch (preset) {
      case 'morning':
        setStartHour(6);
        setEndHour(12);
        break;
      case 'afternoon':
        setStartHour(12);
        setEndHour(18);
        break;
      case 'evening':
        setStartHour(18);
        setEndHour(23);
        break;
      case 'full':
        setStartHour(6);
        setEndHour(23);
        break;
      default:
        break;
    }
  };

  return (
    <DndContext
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      collisionDetection={pointerWithin}
    >
      <Box sx={{ fontFamily }}>
        {/* Grid Controls Toolbar */}
        <Paper
          sx={{
            p: 2,
            mb: 2,
            backgroundColor,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
              alignItems: { xs: 'stretch', md: 'center' },
            }}
          >
            {/* Zoom Controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="body2"
                sx={{ minWidth: 60, color: textColor }}
              >
                Zoom:
              </Typography>
              <IconButton
                size="small"
                onClick={handleZoomOut}
                disabled={timeIncrement === 60}
                title="Zoom Out (Show more hours)"
                sx={{ color: primaryColor }}
              >
                <ZoomOutIcon />
              </IconButton>
              <Typography
                variant="caption"
                sx={{ minWidth: 80, textAlign: 'center', color: textColor }}
              >
                {timeIncrement === 60 && '1 hour'}
                {timeIncrement === 30 && '30 min'}
                {timeIncrement === 15 && '15 min'}
              </Typography>
              <IconButton
                size="small"
                onClick={handleZoomIn}
                disabled={timeIncrement === 15}
                title="Zoom In (Show more detail)"
                sx={{ color: primaryColor }}
              >
                <ZoomInIcon />
              </IconButton>
            </Box>

            {/* Time Range Slider */}
            <Box sx={{ flex: 1, px: { xs: 0, md: 2 } }}>
              <Typography
                variant="body2"
                gutterBottom
                sx={{ color: textColor }}
              >
                Time Range:{' '}
                {formatTimeDisplay(
                  `${startHour.toString().padStart(2, '0')}:00`
                )}{' '}
                -{' '}
                {formatTimeDisplay(`${endHour.toString().padStart(2, '0')}:00`)}
              </Typography>
              <Slider
                value={[startHour, endHour]}
                onChange={handleTimeRangeChange}
                min={0}
                max={24}
                step={1}
                marks={[
                  { value: 0, label: '12 AM' },
                  { value: 6, label: '6 AM' },
                  { value: 12, label: '12 PM' },
                  { value: 18, label: '6 PM' },
                  { value: 24, label: '12 AM' },
                ]}
                valueLabelDisplay="auto"
                valueLabelFormat={value =>
                  formatTimeDisplay(`${value.toString().padStart(2, '0')}:00`)
                }
                sx={{
                  color: primaryColor,
                  '& .MuiSlider-markLabel': {
                    color: textColor,
                  },
                }}
              />
            </Box>

            {/* Quick Range Presets */}
            <Box>
              <Typography
                variant="body2"
                gutterBottom
                sx={{ color: textColor }}
              >
                Quick Range:
              </Typography>
              <ButtonGroup
                size="small"
                variant="outlined"
                sx={{
                  '& .MuiButton-outlined': {
                    borderColor: primaryColor,
                    color: primaryColor,
                    '&:hover': {
                      borderColor: primaryColor,
                      backgroundColor: `${primaryColor}10`,
                    },
                  },
                }}
              >
                <Button onClick={() => handleQuickRange('morning')}>
                  Morning
                </Button>
                <Button onClick={() => handleQuickRange('afternoon')}>
                  Afternoon
                </Button>
                <Button onClick={() => handleQuickRange('evening')}>
                  Evening
                </Button>
                <Button onClick={() => handleQuickRange('full')}>
                  Full Day
                </Button>
              </ButtonGroup>
            </Box>
          </Box>

          {/* Day Selection Controls */}
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              {/* Number of Days */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ minWidth: 100, color: textColor }}
                >
                  Days to Show:
                </Typography>
                <ButtonGroup
                  size="small"
                  variant="outlined"
                  sx={{
                    '& .MuiButton-outlined': {
                      borderColor: primaryColor,
                      color: primaryColor,
                      '&:hover': {
                        borderColor: primaryColor,
                        backgroundColor: `${primaryColor}10`,
                      },
                    },
                    '& .MuiButton-contained': {
                      backgroundColor: primaryColor,
                      '&:hover': {
                        backgroundColor: `${primaryColor}dd`,
                      },
                    },
                  }}
                >
                  {[1, 2, 3, 4, 5, 6, 7].map(num => (
                    <Button
                      key={num}
                      onClick={() => {
                        setNumVisibleDays(num);
                        if (startDayIndex + num > 7) {
                          setStartDayIndex(Math.max(0, 7 - num));
                        }
                      }}
                      variant={
                        numVisibleDays === num ? 'contained' : 'outlined'
                      }
                    >
                      {num}
                    </Button>
                  ))}
                </ButtonGroup>
              </Box>

              {/* Day Navigation (only show if not showing all 7 days) */}
              {numVisibleDays < 7 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ color: textColor }}>
                    Starting Day:
                  </Typography>
                  <ButtonGroup
                    size="small"
                    variant="outlined"
                    sx={{
                      '& .MuiButton-outlined': {
                        borderColor: primaryColor,
                        color: primaryColor,
                        '&:hover': {
                          borderColor: primaryColor,
                          backgroundColor: `${primaryColor}10`,
                        },
                      },
                      '& .MuiButton-contained': {
                        backgroundColor: primaryColor,
                        '&:hover': {
                          backgroundColor: `${primaryColor}dd`,
                        },
                      },
                    }}
                  >
                    {DAYS_OF_WEEK.map((day, index) => {
                      const canShow = index + numVisibleDays <= 7;
                      return (
                        <Button
                          key={day}
                          onClick={() => setStartDayIndex(index)}
                          variant={
                            startDayIndex === index ? 'contained' : 'outlined'
                          }
                          disabled={!canShow}
                          sx={{ minWidth: 40 }}
                        >
                          {day.substring(0, 3)}
                        </Button>
                      );
                    })}
                  </ButtonGroup>
                </Box>
              )}

              {/* Color Code Mode Selector */}
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="color-mode-label" sx={{ color: textColor }}>
                  Color Code By
                </InputLabel>
                <Select
                  labelId="color-mode-label"
                  value={colorMode}
                  label="Color Code By"
                  onChange={e => setColorMode(e.target.value as ColorCodeMode)}
                  sx={{
                    color: textColor,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: primaryColor,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: primaryColor,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: primaryColor,
                    },
                  }}
                >
                  <MenuItem value="styles">Styles</MenuItem>
                  <MenuItem value="teachers">Teachers</MenuItem>
                  <MenuItem value="rooms">Rooms</MenuItem>
                  <MenuItem value="disciplines">Disciplines</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Paper>

        {/* Instruction/Info Alert (only show for editable mode) */}
        {isEditable && visibleClasses.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {scheduledClasses.length === 0
              ? 'No classes scheduled yet. Click on any grid cell to add a class manually.'
              : 'No classes in this time range. Adjust the time range to see scheduled classes.'}
          </Alert>
        )}

        {/* Schedule Grid */}
        <Paper
          sx={{
            overflow: 'auto',
            maxHeight: '600px',
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `80px repeat(${numVisibleDays}, 1fr)`,
              minWidth: `${200 + numVisibleDays * 100}px`,
            }}
          >
            {/* Header Row */}
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                bgcolor: backgroundColor,
                borderBottom: 2,
                borderColor: primaryColor,
                p: 1,
                fontWeight: 'bold',
                color: textColor,
                fontFamily,
                zIndex: 2,
              }}
            >
              Time
            </Box>
            {visibleDays.map(day => (
              <Box
                key={day}
                sx={{
                  position: 'sticky',
                  top: 0,
                  bgcolor: backgroundColor,
                  borderBottom: 2,
                  borderColor: primaryColor,
                  p: 1,
                  textAlign: 'center',
                  fontWeight: 'bold',
                  color: primaryColor,
                  fontFamily,
                  zIndex: 2,
                }}
              >
                {day}
              </Box>
            ))}

            {/* Grid Rows */}
            {timeSlots.map(time => (
              <React.Fragment key={time}>
                {/* Time Label */}
                <Box
                  sx={{
                    position: 'sticky',
                    left: 0,
                    bgcolor: backgroundColor,
                    borderRight: 1,
                    borderBottom: 1,
                    borderColor: 'divider',
                    p: 1,
                    fontSize: '0.875rem',
                    color: textColor,
                    fontFamily,
                    zIndex: 1,
                  }}
                >
                  {formatTimeDisplay(time)}
                </Box>

                {/* Day Cells */}
                {visibleDays.map(day => {
                  const currentSlotIndex = timeSlots.indexOf(time);
                  const nextTime = timeSlots[currentSlotIndex + 1] || '23:59';

                  const classesStartingInCell = visibleClasses.filter(sc => {
                    if (sc.day !== day) return false;
                    const classStartTime = sc.start_time.substring(0, 5);
                    return classStartTime >= time && classStartTime < nextTime;
                  });

                  // Detect concurrent classes and calculate their positioning
                  const classesWithPosition = detectConcurrentClasses(
                    classesStartingInCell
                  );

                  return (
                    <DroppableGridCell
                      key={`${day}-${time}`}
                      day={day}
                      time={time}
                      isEditable={isEditable}
                      onClick={handleCellClick}
                    >
                      {classesWithPosition.map(
                        ({ scheduledClass: sc, columnIndex, totalColumns }) => {
                          const classStartMinutes = timeToMinutes(
                            sc.start_time
                          );
                          const classEndMinutes = timeToMinutes(sc.end_time);
                          const slotStartMinutes = timeToMinutes(time);
                          const slotDurationMinutes = timeIncrement;

                          const offsetMinutes =
                            classStartMinutes - slotStartMinutes;
                          const topPercent =
                            (offsetMinutes / slotDurationMinutes) * 100;

                          const classDurationMinutes =
                            classEndMinutes - classStartMinutes;
                          const heightInSlots =
                            classDurationMinutes / slotDurationMinutes;
                          const heightPx = heightInSlots * 60;

                          // Calculate column positioning for side-by-side display
                          const { leftPercent, widthPercent } =
                            calculateColumnPosition(columnIndex, totalColumns);

                          // Get color for this class based on color mode
                          const blockColor = getColorForClass(
                            sc.class_details,
                            sc.room,
                            sc.teachers,
                            colorMode,
                            colorMap,
                            primaryColor
                          );

                          return (
                            <DraggableClassBlock
                              key={sc.id}
                              scheduledClass={sc}
                              primaryColor={primaryColor}
                              blockColor={blockColor}
                              fontFamily={fontFamily}
                              topPercent={topPercent}
                              heightPx={heightPx}
                              leftPercent={leftPercent}
                              widthPercent={widthPercent}
                              isEditable={isEditable}
                              onClick={handleClassBlockClick}
                            />
                          );
                        }
                      )}
                    </DroppableGridCell>
                  );
                })}
              </React.Fragment>
            ))}
          </Box>
        </Paper>

        {/* Color Legend */}
        <ColorLegend
          scheduledClasses={visibleClasses}
          colorMode={colorMode}
          colorMap={colorMap}
          textColor={textColor}
          backgroundColor={backgroundColor}
        />
      </Box>
    </DndContext>
  );
};

export default ScheduleGrid;
